import * as JSZip from 'jszip';
import {ShapefileImportPart} from '../../classes/shapefile-import.class';
import {addDbfFields, DbfField, DbfValue, readDbfColumn} from './shapefile-dbf';

/** Columns added to every layer of the delivered shapefile, the original columns are kept as they are */
export const VVR_SHAPEFILE_FIELDS: DbfField[] = [
    {name: 'VVR_STATUS', type: 'C', length: 40},
    {name: 'VVR_CROW', type: 'C', length: 5},
    {name: 'VVR_UITGEV', type: 'D', length: 8},
    {name: 'VVR_OPMERK', type: 'C', length: 254},
    {name: 'VVR_PUNAIS', type: 'N', length: 10}
];

// The files of a shapefile layer that are returned, other files in the delivered zip are left out
const SHAPEFILE_EXTENSIONS = ['.shp', '.shx', '.dbf', '.prj', '.cpg'];

/**
 * Returns the originally delivered shapefile zip with the VVR columns added to the DBF of every layer.
 * Only the files of the shapefile layers are returned (see SHAPEFILE_EXTENSIONS); the geometry files are copied
 * unchanged, other files in the delivered zip (e.g. a GeoJSON version) are left out as they are not updated.
 * A record without an imported part (e.g. skipped during import) gets empty VVR columns.
 *
 * With a reference column (e.g. GUID) records are matched on their reference, so parts added by a later import
 * into the same inspection are included too. Otherwise only the parts of this import are matched on record number.
 */
export async function createUpdatedShapefile(original: ArrayBuffer,
                                             parts: ShapefileImportPart[],
                                             shapefileImportId: number,
                                             referenceField?: string): Promise<Uint8Array> {
    const zip = await JSZip.loadAsync(original);

    const partsByReference = new Map<string, ShapefileImportPart>();
    const partsByLayer = new Map<string, Map<number, ShapefileImportPart>>();
    parts.forEach(part => {
        if (referenceField && part.external_reference) {
            partsByReference.set(String(part.external_reference).trim(), part);
        }
        if (+part.shapefile_import_id === +shapefileImportId) {
            if (!partsByLayer.has(part.layer)) {
                partsByLayer.set(part.layer, new Map());
            }
            partsByLayer.get(part.layer).set(+part.record, part);
        }
    });

    // Same naming as the importer: the file name without folder and extension is the layer
    const files = Object.keys(zip.files).filter(path => !zip.files[path].dir);
    const layerOf = (path: string) => path.split('/').pop().replace(/\.(shp|dbf|prj|shx|cpg)$/i, '');
    const findFile = (layer: string, extension: string) =>
        files.find(path => layerOf(path) === layer && path.toLowerCase().endsWith(extension));

    // Leave out everything that is not part of a shapefile layer (a .shp with a .dbf), the importer only used those
    const isLayerFile = (path: string) => SHAPEFILE_EXTENSIONS.some(extension => path.toLowerCase().endsWith(extension))
        && !!findFile(layerOf(path), '.shp') && !!findFile(layerOf(path), '.dbf');
    files.filter(path => !isLayerFile(path)).forEach(path => zip.remove(path));

    for (const dbfPath of files.filter(path => path.toLowerCase().endsWith('.dbf'))) {
        const layer = layerOf(dbfPath);
        if (!findFile(layer, '.shp')) {
            continue;
        }
        const cpgPath = findFile(layer, '.cpg');
        const encoding = cpgPath ? await zip.file(cpgPath).async('string') : undefined;
        const layerParts = partsByLayer.get(layer) ?? new Map<number, ShapefileImportPart>();

        const dbf = await zip.file(dbfPath).async('uint8array');
        const references = referenceField ? readDbfColumn(dbf, referenceField, encoding) : [];
        const partOf = (record: number) => (references[record] && partsByReference.get(references[record])) || layerParts.get(record);
        const updated = addDbfFields(dbf, VVR_SHAPEFILE_FIELDS, record => partValues(partOf(record)), encoding);
        zip.file(dbfPath, updated);
    }

    return zip.generateAsync({type: 'uint8array', compression: 'DEFLATE'});
}

function partValues(part: ShapefileImportPart): DbfValue[] {
    if (!part) {
        return [];
    }
    // MySQL datetime "2026-10-14 11:12:14", the T makes it parse in every browser
    const executedDate = part.executed_date ? new Date(part.executed_date.replace(' ', 'T')) : null;
    return [part.status, part.crow, executedDate, part.description, part.pushpin_number];
}
